// ===========================================
// Impayes — Page liste refondue (UX cohérent avec clients/contrats/reglements)
// ===========================================
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Select, Button, Tag, Grid, message, DatePicker, Dropdown, Empty,
    Modal, Typography, List, Pagination, Spin, Tooltip,
} from 'antd';
import {
    SearchOutlined, ExclamationCircleOutlined, SendOutlined, DownloadOutlined,
    FilePdfOutlined, FileExcelOutlined, CheckCircleOutlined, DollarOutlined,
    CalendarOutlined, WarningOutlined, ClearOutlined, PhoneOutlined,
    UserOutlined, BankOutlined, QuestionCircleOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { impayesService, agencesService, typesAssuranceService } from '../../common/services';
import { useDebounce, useReferentiel, useGlobalAgenceState, useCanSwitchAgence, useUserAgenceId, useExporterName } from '../../common/hooks';
import { resolveAgenceLabel, addExporterFooter, PDF_HEAD_STYLES, PDF_FOOT_STYLES } from '../../common/utils/pdfExport';
import { borderExcelTable, addExcelTotals, addExcelLogoHeader, addExcelExportInfo } from '../../common/utils/excelExport';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatMontant, formatMontantPdf, formatPhone as formatPhoneShared } from '../../common/utils/format';

import MiniKpiCard from '../clients/components/MiniKpiCard';
import Can from '../../components/can';

import './impayes.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

const formatPhone = (tel) => formatPhoneShared(tel, { fallback: '—' });

const computeRetard = (dateEcheance) => {
    if (!dateEcheance) return 0;
    return Math.max(0, Math.floor((Date.now() - new Date(dateEcheance).getTime()) / (1000 * 60 * 60 * 24)));
};

const Impayes = () => {
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    // ─── État ─────────────────────────────────────────────────────────
    const [impayes, setImpayes] = useState([]);
    const [guideOpen, setGuideOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 400);
    const [filterAgence, setFilterAgence] = useGlobalAgenceState();
    const canSwitchAgence = useCanSwitchAgence();
    const userAgenceId = useUserAgenceId();
    const exporterName = useExporterName();
    const [filterDates, setFilterDates] = useState(null);
    const [filterType, setFilterType] = useState('all');
    const { data: agences } = useReferentiel('agences', () => agencesService.getAll());
    const { data: typesAssurance } = useReferentiel('typesAssurance', () => typesAssuranceService.getAll());
    const [summary, setSummary] = useState({ total: 0, montant: 0, countRecent: 0, montantRecent: 0, countRetard30: 0, montantRetard30: 0 });
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const fetchImpayes = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const params = {
                page, pageSize,
                search: debouncedSearch,
                agence: filterAgence,
                typeAssurance: filterType,
                start: filterDates?.[0]?.format('YYYY-MM-DD'),
                end: filterDates?.[1]?.format('YYYY-MM-DD'),
            };
            const res = await impayesService.getAll(params);
            const data = res?.data || res?.items || res || [];
            const meta = Array.isArray(data) ? {} : data;
            setImpayes(Array.isArray(data) ? data : data.items || []);
            setPagination((prev) => ({ ...prev, current: page, pageSize, total: meta.total ?? res?.total ?? data.length }));
            // KPIs calculés par l'API sur toute la liste filtrée (et non sur la seule page affichée)
            setSummary({
                total: meta.total || 0,
                montant: meta.totalImpaye || 0,
                countRecent: meta.countRecent || 0,
                montantRecent: meta.montantRecent || 0,
                countRetard30: meta.countRetard30 || 0,
                montantRetard30: meta.montantRetard30 || 0,
            });
        } catch (error) {
            toast.error('Erreur lors du chargement des impayés');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchImpayes(1, pagination.pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filterAgence, filterType, filterDates]);

    const stats = summary;

    // ─── Filtres ──────────────────────────────────────────────────────
    const activeFiltersCount = useMemo(() => {
        let n = 0;
        if (filterAgence !== 'all') n++;
        if (filterType !== 'all') n++;
        if (filterDates) n++;
        return n;
    }, [filterAgence, filterType, filterDates]);

    const clearAllFilters = () => {
        setSearch('');
        setFilterAgence('all');
        setFilterType('all');
        setFilterDates(null);
    };

    const getTypeNom = () => (filterType !== 'all'
        ? ((typesAssurance || []).find((t) => t._id === filterType)?.nom || '')
        : '');
    const exportTitle = () => {
        const typeNom = getTypeNom();
        return typeNom ? `LISTE DES IMPAYÉS - ${typeNom.toUpperCase()}` : 'LISTE DES IMPAYÉS';
    };

    // ─── SMS Relance ──────────────────────────────────────────────────
    const handleSmsRelance = () => {
        if (selectedRowKeys.length === 0) {
            toast.warning('Veuillez sélectionner au moins un impayé');
            return;
        }
        const selected = impayes.filter((i) => selectedRowKeys.includes(i._id));
        const withPhone = selected.filter((i) => i.client?.telephone || i.client?.phone);
        const withoutPhone = selected.length - withPhone.length;
        const sample = withPhone.slice(0, 3).map((i) =>
            `${i.client?.nom || ''} ${i.client?.prenom || ''}`.trim() || i.numeroPolice,
        );
        const others = withPhone.length - sample.length;

        Modal.confirm({
            title: <span><SendOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Relance SMS groupée</span>,
            width: 520,
            icon: null,
            content: (
                <div>
                    <p style={{ marginBottom: 8 }}>Vous allez envoyer un SMS de relance à <strong>{withPhone.length} client(s)</strong>.</p>
                    {withoutPhone > 0 && (
                        <p style={{ color: '#fa8c16', marginBottom: 8 }}>
                            <ExclamationCircleOutlined style={{ marginRight: 6 }} />
                            {withoutPhone} client(s) sans numéro de téléphone seront ignorés.
                        </p>
                    )}
                    {sample.length > 0 && (
                        <div style={{ background: '#fafafa', padding: 12, borderRadius: 8, marginBottom: 12 }}>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Destinataires :</Typography.Text>
                            <ul style={{ marginBottom: 0, paddingLeft: 20, marginTop: 6 }}>
                                {sample.map((n, i) => <li key={i}>{n}</li>)}
                                {others > 0 && <li style={{ color: '#8c8c8c', fontStyle: 'italic' }}>… et {others} autre(s)</li>}
                            </ul>
                        </div>
                    )}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Le message contiendra le numéro de police, le montant dû et la date d'échéance.
                    </Typography.Text>
                </div>
            ),
            okText: `Envoyer ${withPhone.length} SMS`,
            cancelText: 'Annuler',
            okButtonProps: { disabled: withPhone.length === 0, style: { background: '#1A2B5F', borderColor: '#1A2B5F' } },
            onOk: async () => {
                const hide = message.loading('Envoi des SMS en cours...', 0);
                try {
                    const res = await impayesService.relanceSms({ contratIds: selectedRowKeys });
                    hide();
                    const data = res?.data || res || {};
                    const sent = data.sent ?? withPhone.length;
                    const failed = data.failed ?? 0;
                    if (failed > 0) {
                        toast.warning(`${sent} SMS envoyé(s), ${failed} échec(s)`);
                    } else {
                        toast.success(`${sent} SMS de relance envoyé(s) avec succès`);
                    }
                    setSelectedRowKeys([]);
                } catch (error) {
                    hide();
                    toast.error(error?.message || "Erreur lors de l'envoi des SMS");
                }
            },
        });
    };

    // ─── Exports ──────────────────────────────────────────────────────
    const exportHeaders = ['N° Police', 'Client', 'Téléphone', 'Type assurance', 'Échéance', 'Retard', 'Agence', 'Montant dû'];

    const fetchAllForExport = async () => {
        const hide = message.loading("Préparation de l'export...", 0);
        try {
            const params = {
                page: 1, pageSize: 99999, search, agence: filterAgence, typeAssurance: filterType,
                start: filterDates?.[0]?.format('YYYY-MM-DD'),
                end: filterDates?.[1]?.format('YYYY-MM-DD'),
            };
            const res = await impayesService.getAll(params);
            const data = res?.data || res?.items || res || [];
            const all = Array.isArray(data) ? data : data.items || [];
            hide();
            return all;
        } catch (e) {
            hide();
            toast.error("Erreur lors de la préparation de l'export");
            return [];
        }
    };

    const buildExportData = (list) => list.map((r) => {
        const jours = computeRetard(r.dateEcheance);
        return {
            police: r.numeroPolice || '',
            client: r.client?.nom ? `${r.client.nom} ${r.client.prenom || ''}`.trim() : r.client?.raisonSociale || '',
            telephone: formatPhone(r.client?.telephone),
            type: r.typeAssurance?.nom || '',
            montant: formatMontantPdf(r.soldeImpaye || 0),
            montantNum: r.soldeImpaye || 0,
            echeance: r.dateEcheance ? new Date(r.dateEcheance).toLocaleDateString('fr-FR') : '',
            retard: `${jours} jours`,
            agence: r.agence?.nom || '',
        };
    });

    const handleExportPDF = async () => {
        const allImpayes = await fetchAllForExport();
        if (!allImpayes.length) return;
        const doc = new jsPDF({ orientation: 'landscape' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;

        doc.addImage(logoCap, 'JPEG', 14, 8, 40, 16);
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, pageWidth - 14, 15, { align: 'right' });

        const agenceNom = resolveAgenceLabel({ selected: filterAgence, agences, canSwitchAgence, userAgenceId });
        doc.setFontSize(12);
        doc.text(`AGENCE : ${agenceNom}`, centerX, 15, { align: 'center' });

        doc.setFontSize(14);
        doc.setFont(undefined, 'bolditalic');
        doc.text(exportTitle(), centerX, 30, { align: 'center' });
        doc.setFont(undefined, 'normal');

        // Période de filtre (si renseignée)
        let pdfStartY = 38;
        if (filterDates && filterDates[0] && filterDates[1]) {
            const du = filterDates[0].format('DD/MM/YYYY');
            const au = filterDates[1].format('DD/MM/YYYY');
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            const periodeLabel = 'Période du : ';
            const periodeMid = '   au   ';
            const fullPeriode = `${periodeLabel}${du}${periodeMid}${au}`;
            const totalW = doc.getTextWidth(fullPeriode);
            const startX = centerX - totalW / 2;
            doc.setTextColor(0);
            doc.text(periodeLabel, startX, 38);
            doc.setTextColor(139, 0, 0);
            doc.text(du, startX + doc.getTextWidth(periodeLabel), 38);
            doc.setTextColor(0);
            doc.text(periodeMid, startX + doc.getTextWidth(periodeLabel + du), 38);
            doc.setTextColor(139, 0, 0);
            doc.text(au, startX + doc.getTextWidth(periodeLabel + du + periodeMid), 38);
            doc.setTextColor(0);
            doc.setFont(undefined, 'normal');
            pdfStartY = 44;
        }

        const data = buildExportData(allImpayes);
        const totalMontant = data.reduce((s, d) => s + (d.montantNum || 0), 0);
        autoTable(doc, {
            startY: pdfStartY,
            head: [exportHeaders],
            body: data.map((d) => [d.police, d.client, d.telephone, d.type, d.echeance, d.retard, d.agence, d.montant]),
            styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.3, fillColor: false },
            headStyles: PDF_HEAD_STYLES,
            footStyles: PDF_FOOT_STYLES,
            alternateRowStyles: { fillColor: false },
            columnStyles: { 5: { halign: 'center' }, 7: { halign: 'right' } },
            foot: [[
                { content: 'TOTAL', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
                { content: formatMontantPdf(totalMontant), styles: { halign: 'right', fontStyle: 'bold' } },
            ]],
        });

        addExporterFooter(doc, exporterName);
        doc.save(`impayes_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté avec succès');
    };

    const handleExportExcel = async () => {
        const allImpayes = await fetchAllForExport();
        if (!allImpayes.length) return;
        const data = buildExportData(allImpayes);
        const today = new Date().toLocaleDateString('fr-FR');
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Impayés');
        ws.columns = [{ width: 22 }, { width: 28 }, { width: 18 }, { width: 26 }, { width: 14 }, { width: 12 }, { width: 22 }, { width: 16 }];
        await addExcelLogoHeader(wb, ws);

        const titleRow = ws.addRow([exportTitle()]);
        ws.mergeCells(titleRow.number, 1, titleRow.number, exportHeaders.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        // Période de filtre (si renseignée)
        if (filterDates && filterDates[0] && filterDates[1]) {
            const du = filterDates[0].format('DD/MM/YYYY');
            const au = filterDates[1].format('DD/MM/YYYY');
            const periodeRow = ws.addRow([`Période du ${du} au ${au}`]);
            ws.mergeCells(periodeRow.number, 1, periodeRow.number, exportHeaders.length);
            periodeRow.getCell(1).font = { bold: true, italic: true, size: 11, color: { argb: '8B0000' } };
            periodeRow.getCell(1).alignment = { horizontal: 'center' };
        }

        addExcelExportInfo(ws, { colCount: exportHeaders.length, exporterName, today });
        ws.addRow([]);

        const headerRow = ws.addRow(exportHeaders);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: '000000' }, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        data.forEach((d) => {
            ws.addRow([d.police, d.client, d.telephone, d.type, d.echeance, d.retard, d.agence, d.montant]);
        });
        addExcelTotals(ws, { headerRow: headerRow.number, numericCols: [7], labelCol: 1 });
        borderExcelTable(ws, headerRow.number, exportHeaders.length);
        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `impayes_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté avec succès');
    };

    const exportMenuItems = [
        { key: 'pdf',   label: 'Exporter en PDF',   icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#389e0d' }} />, onClick: handleExportExcel },
    ];

    // ─── Colonnes ─────────────────────────────────────────────────────
    const columns = [
        {
            title: 'N° Police',
            dataIndex: 'numeroPolice',
            key: 'police',
            width: 150,
            render: (t, r) => (
                <button
                    type="button"
                    className="impaye-cell-police"
                    onClick={() => navigate(`/dashboard/contrat/${r._id}`)}
                >
                    {t || '—'}
                </button>
            ),
        },
        {
            title: 'Client',
            key: 'client',
            render: (_, r) => {
                const c = r.client;
                if (!c) return '—';
                const isEntreprise = c.type === 'entreprise';
                const name = c.raisonSociale || `${c.nom || ''} ${c.prenom || ''}`.trim() || '—';
                return (
                    <div className="impaye-cell-client">
                        <div className={`impaye-cell-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                            {isEntreprise ? <BankOutlined /> : <UserOutlined />}
                        </div>
                        <div className="impaye-cell-client-body">
                            <div className="impaye-cell-name">{name}</div>
                            <div className="impaye-cell-phone">
                                <PhoneOutlined /> {formatPhone(c.telephone)}
                            </div>
                        </div>
                    </div>
                );
            },
            width: 280,
            ellipsis: true,
        },
        {
            title: 'Type assurance',
            dataIndex: ['typeAssurance', 'nom'],
            key: 'typeAssurance',
            width: 200,
            ellipsis: true,
            responsive: ['md'],
            render: (v) => (v ? <Tag color="blue">{v}</Tag> : '—'),
        },
        {
            title: 'Échéance',
            dataIndex: 'dateEcheance',
            key: 'dateEcheance',
            width: 130,
            sorter: (a, b) => new Date(a.dateEcheance || 0) - new Date(b.dateEcheance || 0),
            render: (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—',
        },
        {
            title: 'Retard',
            key: 'retard',
            width: 110,
            sorter: (a, b) => computeRetard(a.dateEcheance) - computeRetard(b.dateEcheance),
            render: (_, r) => {
                const jours = computeRetard(r.dateEcheance);
                const color = jours > 30 ? 'red' : jours > 15 ? 'orange' : 'gold';
                return <Tag color={color}>{jours} jour{jours > 1 ? 's' : ''}</Tag>;
            },
        },
        {
            title: 'Agence',
            dataIndex: ['agence', 'nom'],
            key: 'agence',
            width: 180,
            ellipsis: true,
            responsive: ['lg'],
        },
        {
            title: 'Montant dû',
            dataIndex: 'soldeImpaye',
            key: 'montantDu',
            width: 150,
            align: 'right',
            sorter: (a, b) => (a.soldeImpaye || 0) - (b.soldeImpaye || 0),
            render: (v) => <span className="impaye-cell-amount">{formatMontant(v || 0)}</span>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            align: 'center',
            fixed: 'right',
            render: (_, r) => (
                <Tooltip title="Voir le contrat">
                    <Button
                        type="primary"
                        size="small"
                        icon={<SearchOutlined />}
                        className="impaye-action-detail"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/contrat/${r._id}`);
                        }}
                    >
                        Détail
                    </Button>
                </Tooltip>
            ),
        },
    ];

    return (
        <div className="impayes-page">
            {/* Header */}
            <header className="impayes-header">
                <div className="impayes-header-text">
                    <h1 className="impayes-title">
                        <ExclamationCircleOutlined /> Impayés
                    </h1>
                    <p className="impayes-subtitle">Contrats avec solde restant dû</p>
                </div>
                <div className="impayes-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        {!isMobile && 'Guide'}
                    </Button>
                    <Can perm="impayes:relance">
                        <Button
                            icon={<SendOutlined />}
                            onClick={handleSmsRelance}
                            disabled={selectedRowKeys.length === 0}
                            className="impayes-btn-sms"
                        >
                            {!isMobile && `SMS Relance (${selectedRowKeys.length})`}
                            {isMobile && `(${selectedRowKeys.length})`}
                        </Button>
                    </Can>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Gérer les impayés</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={620}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Cette page liste les contrats ayant un <strong>solde restant dû</strong> :
                    elle sert au suivi des recouvrements et aux relances.
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <ExclamationCircleOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Comprendre la liste</strong> — Chaque ligne est un
                                    contrat partiellement ou non payé : montant dû, échéance et
                                    retard sont affichés.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <SearchOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Rechercher &amp; filtrer</strong> — Recherchez par
                                    client ou téléphone, puis filtrez par agence, par type
                                    d'assurance (risque) et par période d'<strong>échéance</strong>.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <SendOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Relance SMS</strong> — Cochez les lignes concernées
                                    puis cliquez sur <strong>« SMS Relance »</strong> pour notifier
                                    automatiquement les clients sélectionnés.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <DollarOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Encaisser un paiement</strong> — L'enregistrement
                                    d'un règlement se fait dans la page <strong>« Règlements »</strong> ;
                                    le contrat sortira des impayés une fois soldé.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <DownloadOutlined style={icStyle} />
                                <div>
                                    <strong>5 · Exporter</strong> — Le bouton « Exporter » génère un
                                    PDF ou un Excel de la liste filtrée pour le suivi.
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs */}
            <section className="impayes-kpis" aria-label="Indicateurs impayés">
                <MiniKpiCard
                    icon={<ExclamationCircleOutlined />}
                    label="Total impayés"
                    value={stats.total}
                    tone="red"
                />
                <MiniKpiCard
                    icon={<DollarOutlined />}
                    label="Montant total"
                    value={formatMontant(stats.montant)}
                    tone="orange"
                />
                <MiniKpiCard
                    icon={<CalendarOutlined />}
                    label="≤ 30 jours"
                    value={stats.countRecent}
                    suffix={stats.montantRecent ? `(${formatMontant(stats.montantRecent)})` : ''}
                    tone="green"
                />
                <MiniKpiCard
                    icon={<WarningOutlined />}
                    label="+ 30 jours"
                    value={stats.countRetard30}
                    suffix={stats.montantRetard30 ? `(${formatMontant(stats.montantRetard30)})` : ''}
                    tone="red"
                />
            </section>

            {/* Carte principale */}
            <Card className="impayes-main-card">
                <div className="impayes-toolbar">
                    <Input
                        allowClear
                        placeholder="Rechercher (n° police, client, téléphone...)"
                        prefix={<SearchOutlined style={{ color: 'var(--cap-text-muted)' }} />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="impayes-search"
                        aria-label="Rechercher un impayé"
                    />
                    {canSwitchAgence && (
                        <Select
                            value={filterAgence}
                            onChange={setFilterAgence}
                            className="impayes-filter"
                            aria-label="Filtrer par agence"
                        >
                            <Option value="all">Toutes les agences</Option>
                            {agences.map((a) => (
                                <Option key={a._id} value={a._id}>{a.nom}</Option>
                            ))}
                        </Select>
                    )}
                    <Select
                        value={filterType}
                        onChange={setFilterType}
                        className="impayes-filter"
                        aria-label="Filtrer par type d'assurance"
                    >
                        <Option value="all">Tous les types d'assurance</Option>
                        {(typesAssurance || []).map((t) => (
                            <Option key={t._id} value={t._id}>{t.nom}</Option>
                        ))}
                    </Select>
                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Échéance début', 'Échéance fin']}
                        onChange={setFilterDates}
                        value={filterDates}
                        className="impayes-filter-dates"
                        allowClear
                    />
                    {(activeFiltersCount > 0 || search) && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="impayes-btn-clear"
                        >
                            Effacer
                        </Button>
                    )}
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} className="impayes-btn-export">
                            Exporter
                        </Button>
                    </Dropdown>
                </div>

                {isMobile ? (
                    <Spin spinning={loading}>
                        {impayes.length === 0 ? (
                            <Empty
                                image={<CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />}
                                description={<span style={{ color: '#389e0d', fontWeight: 500 }}>Aucun impayé — tout est à jour !</span>}
                                style={{ padding: 32 }}
                            />
                        ) : (
                            <List
                                dataSource={impayes}
                                rowKey="_id"
                                split={false}
                                renderItem={(r) => {
                                    const c = r.client || {};
                                    const isEntreprise = c.type === 'entreprise';
                                    const name = c.raisonSociale || `${c.nom || ''} ${c.prenom || ''}`.trim() || '—';
                                    const jours = computeRetard(r.dateEcheance);
                                    const retardColor = jours > 30 ? 'red' : jours > 15 ? 'orange' : 'gold';
                                    return (
                                        <Card
                                            key={r._id}
                                            size="small"
                                            className={`impayes-mobile-card retard-${retardColor}`}
                                            onClick={() => navigate(`/dashboard/contrat/${r._id}`)}
                                        >
                                            <div className="mobile-card-row">
                                                <div className={`impaye-cell-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                                                    {isEntreprise ? <BankOutlined /> : <UserOutlined />}
                                                </div>
                                                <div className="mobile-card-body">
                                                    <div className="mobile-card-head">
                                                        <span className="mobile-card-police">{r.numeroPolice || '—'}</span>
                                                        <Tag color={retardColor}>{jours} jour{jours > 1 ? 's' : ''}</Tag>
                                                    </div>
                                                    <div className="mobile-card-name">{name}</div>
                                                    <div className="mobile-card-phone"><PhoneOutlined /> {formatPhone(c.telephone)}</div>
                                                    <div className="mobile-card-footer">
                                                        <span className="impaye-cell-amount">{formatMontant(r.soldeImpaye || 0)}</span>
                                                        <span className="mobile-card-echeance">
                                                            <CalendarOutlined /> {r.dateEcheance ? new Date(r.dateEcheance).toLocaleDateString('fr-FR') : '—'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    );
                                }}
                            />
                        )}
                        <div className="impayes-pagination-wrapper">
                            <Pagination
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                total={pagination.total}
                                onChange={(p, ps) => fetchImpayes(p, ps)}
                                size="small"
                                simple
                            />
                        </div>
                    </Spin>
                ) : (
                    <Table
                        dataSource={impayes}
                        columns={columns}
                        rowKey="_id"
                        loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                        pagination={{
                            ...pagination,
                            onChange: (p, ps) => fetchImpayes(p, ps),
                            showSizeChanger: true,
                            showTotal: (t, range) => `${range[0]}-${range[1]} sur ${t} impayé${t > 1 ? 's' : ''}`,
                        }}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        locale={{
                            emptyText: loading ? (
                                <div style={{ minHeight: 220 }} />
                            ) : (
                                <Empty
                                    image={<CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />}
                                    description={<span style={{ color: '#389e0d', fontWeight: 500 }}>Aucun impayé — tout est à jour !</span>}
                                />
                            ),
                        }}
                    />
                )}
            </Card>
        </div>
    );
};

export default Impayes;
